import React, { FC, HTMLAttributes } from "react";
import { Button, Icon } from "@clarkinc/zest-design-system/ui";
import { cls } from "@wss/cls";
import { Image } from "@wss/image";
import { ComparisonItem } from "./types";
import { MAX_ITEMS_TO_COMPARE } from "./constants";
import EmptyItem from "./components/EmptyItem";
import spritePath from "~/common/Icons/Zest/remixicon.symbol.v1.svg";

export interface CompareProductsProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "itemRef"> {
  items: ComparisonItem[];
  onRemove: (itemNumber: string, index: number) => void;
  onClear: () => void;
  itemRefs: React.MutableRefObject<HTMLButtonElement[]>;
}

const CompareProducts: FC<CompareProductsProps> = ({
  className,
  items,
  onRemove,
  onClear,
  itemRefs,
}) => {
  const compareButtonUrl = `/compareproducts/?items=${items
    .map((item) => item.itemNumber)
    .join(",")}`;

  // Minus 1 because the first empty item has some text that we just render after the selected items have been rendered
  const emptyItems = MAX_ITEMS_TO_COMPARE - items.length - 1;

  return (
    <div
      className={cls(
        "hidden md:block",
        "px-5 pt-5 pb-4 border-solid border border-[#D8DBDF] bg-white",
        className,
      )}
      data-testid="compare-products-drawer"
    >
      <section
        className="font-bold text-2xl leading-7"
        aria-label="Compare products"
      >
        Compare Products
      </section>
      <div className="font-normal text-base leading-5">
        Choose up to four products to see a side-by-side comparison
      </div>
      <ul className="flex justify-between mt-4 ml-0 list-none">
        {items.map((item, index) => (
          <li
            key={item.itemNumber}
            className="box-border flex relative justify-center items-start w-1/4 h-26 mr-1 border border-solid rounded pt-5 border-gray-300 bg-white shadow"
          >
            <Image
              className="object-top h-10 w-10 lg:h-1/2 lg:w-1/2 lz:h-3/5 lz:w-3/5 xl:h-16 xl:w-16 pl-2 lg:pl-4"
              src={item.itemImage}
              alt=""
              height="200"
              width="200"
            />
            <div className="relative pl-2 pr-4 lg:pr-7">
              <a
                className="text-green-500 font-bold text-2xs !leading-3 text-ellipsis line-clamp-4 break-all hover:underline lg:text-xs"
                href={item.itemLink}
              >
                {item.itemTitle}
              </a>
              <div className="text-ellipsis line-clamp-1 break-all pt-2 font-normal text-[10px] leading-tight">
                #{item.itemNumber.toLocaleUpperCase()}
              </div>
            </div>
            <Button
              className="absolute w-3 top-0 right-0 pr-1 pt-1"
              aria-label={`Delete ${item.itemTitle} from the comparison selection`}
              data-testid={`remove-item-${item.itemNumber}`}
              onClick={() => onRemove(item.itemNumber, index)}
              ref={(el) => {
                if (el) itemRefs.current[index] = el;
              }}
            >
              <Icon
                width="12"
                height="12"
                name="ri-close-line"
                spriteSheetHref={spritePath}
              />
            </Button>
          </li>
        ))}

        {items.length < MAX_ITEMS_TO_COMPARE && (
          <EmptyItem className="text-sm leading-4">
            {emptyItems > 0
              ? `Add up to ${emptyItems + 1} items`
              : "Add up to 1 item"}
          </EmptyItem>
        )}

        {Array.from(
          { length: emptyItems },

          (_, index) => (
            <EmptyItem key={index} />
          ),
        )}
        {items.length > 1 && (
          <div className="flex flex-col gap-y-2 ml-auto justify-center items-center flex-grow-0 pl-5 pr-1">
            <a
              data-testid="compare-button"
              href={compareButtonUrl}
              className="btn btn-small bg-green-500 text-white font-semibold text-base xl:px-7 xl:py-2.5 xl:rounded-normal"
              aria-label="Compare products"
            >
              Compare
            </a>
            <button
              data-testid="clear-all-button"
              type="button"
              className="btn border-none shadow-none bg-transparent text-green-500 underline font-semibold text-base p-0 xl:mx-7"
              aria-label="Clear all products"
              onClick={onClear}
            >
              Clear All
            </button>
          </div>
        )}
      </ul>
    </div>
  );
};

export default CompareProducts;
